import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { USE_REAL_API } from '@/services/api-client';
import * as approvalApi from '@/services/approval-api';

export type ApprovalTab = 'pending' | 'mine' | 'history';
export type ApprovalPriority = 'urgent' | 'normal' | 'expense';

export interface ApprovalItem {
  id: string;
  bizType: string;
  /** i18n key for the type label, e.g. "approvals.type_leave" */
  typeKey: string;
  title: string;
  /** ISO timestamp; card renders relative time. */
  createdAt: string;
  icon: string;
  iconColorVar: string;
  iconBgVar: string;
  priority: ApprovalPriority;
  requesterName: string;
  requesterDept: string;
  requesterInitials: string;
  amount: string;
  /** State after action; undefined = still pending */
  resolution?: 'approved' | 'rejected';
}

interface ApprovalGroups {
  pending: ApprovalItem[];
  mine: ApprovalItem[];
  history: ApprovalItem[];
}

function isoHoursAgo(h: number): string {
  return new Date(Date.now() - h * 3_600_000).toISOString();
}

const MOCK: ApprovalGroups = {
  pending: [
    {
      id: 'L-001',
      bizType: 'HR_LEAVE',
      typeKey: 'approvals.type_leave_urgent',
      title: 'Annual Leave — 3 Days',
      createdAt: isoHoursAgo(2),
      icon: 'event_busy',
      iconColorVar: 'var(--status-alert)',
      iconBgVar: 'var(--status-alert-bg)',
      priority: 'urgent',
      requesterName: 'Lily Mao',
      requesterDept: 'Finance · Full-time',
      requesterInitials: 'LM',
      amount: '3 days',
    },
    {
      id: 'E-002',
      bizType: 'HR_EXPENSE',
      typeKey: 'approvals.type_expense',
      title: 'Business Travel — Tokyo',
      createdAt: isoHoursAgo(5),
      icon: 'receipt_long',
      iconColorVar: 'var(--status-pending)',
      iconBgVar: 'var(--status-pending-bg)',
      priority: 'expense',
      requesterName: 'Riku Kato',
      requesterDept: 'Production · Senior',
      requesterInitials: 'RK',
      amount: '¥38,500',
    },
    {
      id: 'O-003',
      bizType: 'HR_OVERTIME',
      typeKey: 'approvals.type_overtime',
      title: 'Weekend Overtime +4h',
      createdAt: isoHoursAgo(26),
      icon: 'more_time',
      iconColorVar: 'var(--primary)',
      iconBgVar: 'var(--primary-subtle)',
      priority: 'normal',
      requesterName: 'Chen Hui',
      requesterDept: 'Production · Manager',
      requesterInitials: 'CH',
      amount: '4 hours',
    },
    {
      id: 'AP-004',
      bizType: 'AP_MATCH_EXCEPTION',
      typeKey: 'approvals.type_ap_match',
      title: 'Supplier 3-way Match · Kingdee Ink',
      createdAt: isoHoursAgo(0.2),
      icon: 'payments',
      iconColorVar: 'var(--status-pending)',
      iconBgVar: 'var(--status-pending-bg)',
      priority: 'expense',
      requesterName: '王会计',
      requesterDept: 'Finance · AP',
      requesterInitials: 'WJ',
      amount: '¥182,400',
    },
  ],
  mine: [],
  history: [],
};

async function fetchTab(tab: ApprovalTab): Promise<ApprovalItem[]> {
  if (!USE_REAL_API) {
    await new Promise((r) => setTimeout(r, 80));
    return MOCK[tab];
  }
  if (tab === 'pending') return approvalApi.fetchPendingTasks();
  if (tab === 'mine') return approvalApi.fetchMySubmissions();
  return approvalApi.fetchHistory();
}

export function useApprovals(tab: ApprovalTab) {
  return useQuery({
    queryKey: ['approvals', tab, USE_REAL_API],
    queryFn: () => fetchTab(tab),
    staleTime: 30_000,
  });
}

export function useApprovalCounts() {
  return useQuery({
    queryKey: ['approvals', 'counts', USE_REAL_API],
    queryFn: async () => {
      if (USE_REAL_API) {
        const pending = await approvalApi.fetchPendingTasks();
        return {
          pending: pending.filter((i) => !i.resolution).length,
          mine: 0,
          history: 0,
        };
      }
      await new Promise((r) => setTimeout(r, 40));
      return {
        pending: MOCK.pending.filter((i) => !i.resolution).length,
        mine: MOCK.mine.length,
        history: MOCK.history.length,
      };
    },
    staleTime: 30_000,
  });
}

export function useResolveApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
      comment,
    }: {
      id: string;
      action: 'approved' | 'rejected';
      comment?: string;
    }) => {
      if (USE_REAL_API) {
        if (action === 'approved') await approvalApi.approveTask(id, comment ?? '');
        else await approvalApi.rejectTask(id, comment ?? 'Rejected');
      } else {
        await new Promise((r) => setTimeout(r, 250));
        const item = MOCK.pending.find((i) => i.id === id);
        if (item) item.resolution = action;
      }
      return { id, action };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['approvals'] });
    },
  });
}
